// useEditor.js
import { useEffect, useState, useRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import List from '@editorjs/list';
import Table from '@editorjs/table';
import Code from '@editorjs/code';
import Image from '@editorjs/image';
import { MarkdownTable, uploadImageToDirectus } from '../../features/articles';


export default function useEditor(
  editorRef, 
  autosaveTimeoutRef,
  handleSave,
  {
    articleData = null,
    setEditorHasContent = () => {},
    setUserHasEditedEditor = () => {},
    onEditorChange = () => {},
    editorMountKey = null,
    shouldInitialize = true,
    generatedArticleId = null,
    isLocalhost = () => false,
    isAutosaving = false,  // Add this parameter
    forceRefresh = false,  // Force refresh after generation
    paragraphPlaceholder = 'Write your article context here...'
  } = {}
) {
  const editorDebugEnabled = isLocalhost() || window.localStorage.getItem('editor_debug') === '1';
  const debugLog = (...args) => {
    if (editorDebugEnabled) {
      console.log('[EditorJS Debug]', ...args);
    }
  };

  // Store handleSave in ref so it's stable
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const onEditorChangeRef = useRef(onEditorChange);
  onEditorChangeRef.current = onEditorChange;
  // Debug removed - too verbose

  useEffect(() => {
    console.log('🔧 USE_EDITOR: Initializing editor...');
    
    let timers = [];
    let holderClickHandler = null;
    if (!shouldInitialize) {
      if (editorRef.current && editorRef.current.destroy) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
      return undefined;
    }
    
    // Wait for DOM element with retry logic
    const initializeWithRetry = (attempt = 1, maxAttempts = 10) => {
      const editorElement = document.getElementById('editorjs');
      
      if (editorElement) {
      }

      // Only initialize if the element exists and editor isn't already initialized
      // OR if CreateArticle.js cleared the editorRef (after generation)
      if (editorElement && (!editorRef.current || editorRef.current === null)) {
        try {
          console.log('🔧 EDITOR: Initializing EditorJS...');
          
          // Debug: Check what data is being used
          const rawEditorData = articleData?.article_draft ?
            (typeof articleData.article_draft === 'string' ?
              JSON.parse(articleData.article_draft) :
              articleData.article_draft) :
            {};
          const hasInitialBlocks = Array.isArray(rawEditorData?.blocks) && rawEditorData.blocks.length > 0;
          const editorData = hasInitialBlocks
            ? rawEditorData
            : {
                time: Date.now(),
                blocks: [{ type: 'paragraph', data: { text: '' } }],
                version: rawEditorData?.version || '2.29.1'
              };
          
          console.log('📊 EDITOR: EditorJS data being used:', JSON.stringify(editorData, null, 2));
          console.log('📊 EDITOR: EditorJS data blocks count:', editorData.blocks?.length || 0);
          
          editorRef.current = new EditorJS({
            holder: 'editorjs',
            defaultBlock: 'paragraph',
            tools: {
              header: {
                class: Header,
                inlineToolbar: ['link'],
                config: {
                  placeholder: 'Enter a header',
                  levels: [1, 2, 3],
                  defaultLevel: 2
                }
              },
              paragraph: {
                class: Paragraph,
                inlineToolbar: true,
                config: {
                  placeholder: paragraphPlaceholder
                }
              },
              list: {
                class: List,
                inlineToolbar: true,
              },
              table: {
                class: Table,
                inlineToolbar: true,
                config: {
                  rows: 2,
                  cols: 3,
                },
              },
              markdownTable: {
                class: MarkdownTable,
              },
              code: {
                class: Code,
                config: {
                  placeholder: 'Enter code here',
                  language: 'auto' // or specify default language: 'javascript', 'php', 'html', etc.
                }
              },
              image: {
                class: Image,
                config: {
                  uploader: {
                    uploadByFile: async (file) => {
                      try {
                        const result = await uploadImageToDirectus(file);
                        if (!result?.success || !result?.file?.url) {
                          return { success: 0, message: result?.error || 'Upload failed' };
                        }
                        return {
                          success: 1,
                          file: {
                            url: result.file.url,
                            id: result.file.id
                          }
                        };
                      } catch (error) {
                        return { success: 0, message: error.message || 'Upload failed' };
                      }
                    },
                    uploadByUrl: async (url) => ({
                      success: 1,
                      file: {
                        url: String(url || '').replace('http://', 'https://'),
                        id: `url-${Date.now()}`
                      }
                    })
                  }
                }
              }
            },
            onChange: () => {
              if (editorRef.current && editorRef.current.save) {
                editorRef.current.save().then((outputData) => {
                  setEditorHasContent(outputData.blocks.length > 0);
                  setUserHasEditedEditor(true);
                  onEditorChangeRef.current(outputData);
                  
                  if (autosaveTimeoutRef.current) {
                    clearTimeout(autosaveTimeoutRef.current);
                  }
                  
                  if (generatedArticleId) {
                    autosaveTimeoutRef.current = setTimeout(() => handleSaveRef.current(), 2000);
                  }
                });
              }
            },
            data: editorData,
            onReady: () => {
              console.log('✅ EDITOR: EditorJS is ready with', editorData.blocks?.length || 0, 'blocks');
              const holderNode = document.getElementById('editorjs');
              if (holderNode) {
                const shouldHandleClickToFocus = (target) => {
                  if (!target) return false;

                  // Never override clicks on toolbars/popovers/settings/buttons/inputs.
                  const ignoredSelectors = [
                    '.ce-toolbar',
                    '.ce-inline-toolbar',
                    '.ce-popover',
                    '.ce-conversion-toolbar',
                    '.ce-settings',
                    '.cdx-settings-button',
                    'button',
                    'a',
                    'input',
                    'textarea',
                    '[contenteditable="true"]'
                  ];

                  if (ignoredSelectors.some((selector) => target.closest(selector))) {
                    return false;
                  }

                  // Only force focus when clicking editor chrome/background.
                  return target === holderNode
                    || !!target.closest('.codex-editor')
                    || !!target.closest('.codex-editor__redactor');
                };

                holderClickHandler = (event) => {
                  if (!editorRef.current?.caret) return;
                  const target = event.target;
                  const scrollBefore = window.scrollY;

                  const shouldForceFocus = shouldHandleClickToFocus(target);
                  debugLog('click', {
                    targetTag: target?.tagName,
                    targetClass: target?.className,
                    scrollBefore,
                    shouldForceFocus
                  });

                  if (!shouldForceFocus) {
                    return;
                  }

                  setTimeout(async () => {
                    try {
                      const outputData = await editorRef.current.save();
                      const blocksCount = outputData?.blocks?.length || 0;
                      const lastBlockIndex = Math.max(0, blocksCount - 1);
                      editorRef.current.caret.setToBlock(lastBlockIndex, 'end');
                      debugLog('caret.setToBlock', {
                        blocksCount,
                        lastBlockIndex,
                        scrollAfter: window.scrollY,
                        scrollDelta: window.scrollY - scrollBefore
                      });
                    } catch (error) {
                      debugLog('caret.setToBlock failed', error?.message || error);
                    }
                  }, 0);
                };
                holderNode.addEventListener('click', holderClickHandler);
              }
            },
          });
        } catch (error) {
          console.log('❌ EDITOR: Error initializing EditorJS:', error.message);
        }
      } else if (!editorElement && attempt < maxAttempts) {
        // Element not found, try again after delay
        const timer = setTimeout(() => {
          initializeWithRetry(attempt + 1, maxAttempts);
        }, 500);
        timers.push(timer);
      } else if (!editorElement) {
        console.log('❌ USE_EDITOR: EditorJS element not found after all attempts');
      }
    };
    
    // Start the retry process
    initializeWithRetry();

    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      const holderNode = document.getElementById('editorjs');
      if (holderNode && holderClickHandler) {
        holderNode.removeEventListener('click', holderClickHandler);
      }
      
      if (editorRef.current && editorRef.current.destroy) {
        console.log('🗑️ EDITOR: Destroying EditorJS instance in cleanup');
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by article, active editor step and refresh flag
  }, [articleData?.id, forceRefresh, editorMountKey, shouldInitialize]); // Re-render when article/step/refresh changes

  // DISABLED: Separate effect to handle articleData changes
  // This was causing EditorJS to reset user input during autosave
  // useEffect(() => {
  //   console.log('🔄 EDITOR: Article data update effect DISABLED to preserve user input');
  // }, [articleData?.article_draft, setEditorHasContent, isAutosaving]);

  // Track the article_draft content hash to detect real changes
  const [lastDraftHash, setLastDraftHash] = useState(null);
  
  // Only re-render if the content actually changed (not just state reference)
  useEffect(() => {
    if (!articleData?.article_draft || isAutosaving) {
      return;
    }
    
    const currentHash = JSON.stringify(articleData.article_draft);
    if (currentHash !== lastDraftHash) {
      // Removed debug log
      setLastDraftHash(currentHash);
    } else {
      // Removed debug log
    }
  }, [articleData?.article_draft, isAutosaving, lastDraftHash]);

  // Debug: Log when articleData changes
  useEffect(() => {
    // Removed debug logs
  }, [articleData]);
}